/**
 * J6: the walk of นำทาง (systems/nav.ts) on the ground: gold dots along the player's path, an end marker, and an
 * arrow at the edge of the screen when the goal is off it. Square pixels on the scene grid, no glow, nothing
 * allocated per frame.
 */
import { navOf } from '@nine/shared';
import { registerRenderPass } from './registry';

const GOLD = '#ecbe54';
const DARK = '#2a1408';
const STEP = 12; // art pixels between dots
const MAX_DOTS = 60;
const EDGE = 14; // art pixels from the edge of the view

registerRenderPass({
  id: 'navRoute',
  layer: 'labels',
  order: 120,
  draw({ mg, world, iso, ox, oy, px, visible }) {
    const st = navOf(world)?.state;
    if (!st) return;
    const p = world.player;
    if (!p) return;
    const s = Math.max(1, Math.round(px)); // one art pixel
    const dot = (ax: number, ay: number, n: number, c: string): void => {
      mg.fillStyle = c;
      mg.fillRect(Math.round(ax * px) - n * s, Math.round(ay * px) - n * s, 2 * n * s, 2 * n * s);
    };
    // dots along the path, one every STEP art pixels
    let [fx, fy] = iso.proj(p.x, p.y);
    fx += ox;
    fy += oy;
    let acc = 0;
    let dots = 0;
    const path = p.path;
    if (path) {
      for (let i = 0; i < path.length && dots < MAX_DOTS; i++) {
        const [nx0, ny0] = iso.proj(path[i]![0], path[i]![1]);
        const nx = nx0 + ox;
        const ny = ny0 + oy;
        const d = Math.hypot(nx - fx, ny - fy);
        if (d > 0) {
          let t = STEP - acc;
          while (t <= d && dots < MAX_DOTS) {
            const k = t / d;
            dot(fx + (nx - fx) * k, fy + (ny - fy) * k, 1, GOLD);
            dots++;
            t += STEP;
          }
          acc = (acc + d) % STEP;
        }
        fx = nx;
        fy = ny;
      }
    }
    // the goal
    const [gx0, gy0] = iso.proj(st.x, st.y);
    const gx = gx0 + ox;
    const gy = gy0 + oy;
    const v = visible;
    const inside = gx > v.x + EDGE && gx < v.x + v.w - EDGE && gy > v.y + EDGE && gy < v.y + v.h - EDGE;
    if (inside) {
      const bob = Math.floor(performance.now() / 300) % 2; // a 1-art-pixel hop
      dot(gx, gy - 8 - bob, 3, DARK);
      dot(gx, gy - 8 - bob, 2, GOLD);
      dot(gx, gy - 8 - bob, 1, DARK);
      return;
    }
    // an arrow at the edge, pointing toward the goal
    const cx = v.x + v.w / 2;
    const cy = v.y + v.h / 2;
    const dx = gx - cx;
    const dy = gy - cy;
    const k = Math.min((v.w / 2 - EDGE) / Math.abs(dx || 1e-6), (v.h / 2 - EDGE) / Math.abs(dy || 1e-6));
    const ax = cx + dx * k;
    const ay = cy + dy * k;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    // a square-pixel arrow: a shaft of 5 squares with a 3-square head
    for (let i = -2; i <= 2; i++) dot(ax + ux * i * 2, ay + uy * i * 2, 1, i === 2 ? DARK : GOLD);
    dot(ax + ux * 6, ay + uy * 6, 2, DARK);
    dot(ax + ux * 6, ay + uy * 6, 1, GOLD);
  },
});
